import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { selectCompany, setCompany } from '@/store/slices/companySlice';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { INDIAN_STATES } from '@/lib/constants';

export default function BusinessSettingsPage() {
  const dispatch = useDispatch();
  const company = useSelector(selectCompany);
  const { data } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();

  const [form, setForm] = useState({
    name: '', slug: '', state: 'MH', fiscal_year_start: 'APRIL', timezone: 'Asia/Kolkata',
    cin: '', pan: '', gstin: '', address: '', contact_email: '', contact_phone: '',
  });

  useEffect(() => {
    const d = data?.data;
    if (d) {
      setForm({
        name: d.name || '',
        slug: d.slug || '',
        state: d.state || 'MH',
        fiscal_year_start: d.fiscal_year_start || 'APRIL',
        timezone: d.timezone || 'Asia/Kolkata',
        cin: d.cin || '',
        pan: d.pan || '',
        gstin: d.gstin || '',
        address: d.address || '',
        contact_email: d.contact_email || '',
        contact_phone: d.contact_phone || '',
      });
    } else if (company.name) {
      setForm(f => ({ ...f, name: company.name || '', slug: company.slug || '', state: company.state || 'MH', fiscal_year_start: company.fiscal_year_start || 'APRIL', timezone: company.timezone || 'Asia/Kolkata' }));
    }
  }, [data, company.name]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSave = () => {
    mutate(form, {
      onSuccess: () => {
        dispatch(setCompany({ ...company, name: form.name, slug: form.slug, state: form.state, fiscal_year_start: form.fiscal_year_start, timezone: form.timezone }));
        toast.success('Business profile saved');
      },
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-xl">
      <PageHeader title="Business Profile" breadcrumb={[{label:'Settings',href:'/settings'},{label:'Business Profile'}]}/>
      <div className="card space-y-4 p-5">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">Company Information</h3>
        <Input label="Company Name" value={form.name} onChange={set('name')} hint="As registered with MCA"/>
        <Input label="Company Slug" value={form.slug} onChange={set('slug')} prefix="attendx.app/"/>
        <Select label="State" value={form.state} onChange={set('state')} options={INDIAN_STATES.map(s=>({value:s.code,label:s.name}))}/>
        <Select label="Fiscal Year Start" value={form.fiscal_year_start} onChange={set('fiscal_year_start')} options={[{value:'APRIL',label:'April (India standard)'},{value:'JANUARY',label:'January'},{value:'JULY',label:'July'}]}/>
        <Select label="Timezone" value={form.timezone} onChange={set('timezone')} options={[{value:'Asia/Kolkata',label:'IST – Asia/Kolkata'}]}/>
      </div>
      <div className="card space-y-4 p-5">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">Legal & Tax</h3>
        <div className="grid grid-cols-2 gap-4">
          <Input label="CIN" value={form.cin} onChange={set('cin')} placeholder="U72900MH2020PTC123456"/>
          <Input label="PAN" value={form.pan} onChange={set('pan')} placeholder="AAACN1234C"/>
        </div>
        <Input label="GSTIN" value={form.gstin} onChange={set('gstin')} placeholder="27AAACN1234C1Z5"/>
        <Input label="Registered Address" value={form.address} onChange={set('address')}/>
      </div>
      <div className="card space-y-4 p-5">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">Contact</h3>
        <Input label="Contact Email" value={form.contact_email} onChange={set('contact_email')} type="email"/>
        <Input label="Contact Phone" value={form.contact_phone} onChange={set('contact_phone')} type="tel"/>
      </div>
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save Changes'}</Button>
      </div>
    </div>
  );
}
